import React, { ReactNode, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/solid';
import Button from './Button';

interface ModalProps {
  /** Accessible name for the dialog. Rendered as its <h1>. */
  title: string;
  close: () => void;
  children: ReactNode;
  /** Rendered in the footer. Omit for a modal with no committing action. */
  footer?: ReactNode;
  /** Wider container for content that needs to lay out in columns. */
  wide?: boolean;
  className?: string;
}

// React 17 has no `useId`, and the dialog needs a stable id to point
// `aria-labelledby` at.
let nextId = 0;

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * The modal shell for the booking flow.
 *
 * The markup it replaced was a plain `<div onClick={close}>` wrapping another
 * div: no `role="dialog"`, no `aria-modal`, no accessible name, no focus
 * trap, no Escape, no focus return and no scroll lock. A screen reader was
 * never told a dialog had opened, and a keyboard user could tab straight out
 * of it into the page behind — which is still rendered and still scrollable.
 *
 * Class names are unchanged (`modern-modal__*`), and the two timeslot modals
 * now render through here too, so every modal in the booking flow gets the
 * same semantics from one place.
 */
export default function Modal({
  title,
  close,
  children,
  footer,
  wide,
  className,
}: ModalProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`modal-title-${(nextId += 1)}`);

  // Callers pass `close` inline — `close={() => setShowThing(false)}` — so it
  // is a fresh function on every render of the page behind. Depending on it
  // would tear the whole effect down and rebuild it whenever that page
  // re-rendered, and the teardown hands focus back to whatever opened the
  // modal. The return-timeslot modal does exactly that: booking a slot sets
  // state on the dashboard, which would have pulled focus out of the dialog at
  // the moment the confirmation appeared. Read it through a ref instead, so
  // the effect runs once per open.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    const container = containerRef.current;
    // Where focus came from, so it can be handed back on close. Without
    // this, dismissing a modal drops the keyboard user at the top of the
    // document and they have to tab back to where they were.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // `hidden` rather than `offsetParent`: offsetParent is a layout question,
    // and anything that does not lay out — jsdom, a print context — reports
    // null for every element and empties the trap.
    const focusables = () =>
      Array.from(
        container?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      ).filter((el) => !el.closest('[hidden]'));

    // Focus the dialog itself rather than its first control, so the screen
    // reader reads the title before the customer is dropped on a button.
    container?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const elements = focusables();
      if (!elements.length) {
        event.preventDefault();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus();
    };
  }, []);

  return ReactDOM.createPortal(
    <div className="modern-modal__overlay" onClick={close}>
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        tabIndex={-1}
        className={[
          'modern-modal__container',
          wide ? 'modern-modal__container--wide' : null,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modern-modal__header">
          <h1 id={titleId.current}>{title}</h1>
          <Button
            variant="tertiary"
            size="sm"
            iconOnly
            className="modern-modal__close"
            aria-label="Close"
            onClick={close}
          >
            <XMarkIcon aria-hidden="true" />
          </Button>
        </div>

        {children}

        {footer && <div className="modern-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
